import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container, Form, Panel, Schema, Stack } from 'rsuite'
import { useTranslation } from 'react-i18next'
import Format, { DisableOverlay, PasswordField, SelectField, StaticField, TextField } from '@client/components/FormControls'
import UserModel, { userTabName } from '../../models/user'
import {
    Button,
    TabListFooterErrorStyle,
    TabListFooterErrorWrapperStyle,
    TabListFooterStyle,
    TTabListFormData,
} from '@client/components/Controls'
import UserProjectRelation, { RelationType } from '@/client/components/Relation/UserProjectRelation'
import { roleTypeList } from '@shared/roles'
import { RoleType } from '@/shared'
import tabStore, { findTab, setTabName } from '@client/stores/tabsStore'
import { useSyncedState } from '@client/utils/useSyncedState.ts'
import toastMessage from '@/client/utils/toastMessage'
import { serverLog } from '@client/utils/serverLog.ts'
import { user } from '@client/stores/authStore.ts'
import { useEditLock } from '@client/socket/useEditLock.tsx'
import { getSocket } from '@client/socket/socket.ts'
import { useTabDirty, useTabSaveHandler } from '@client/utils/useTabDirty.ts'

const { StringType } = Schema.Types

const userModel = new UserModel()

type EditUserProps = { tabId: string; onCreated?: (newUser: any) => void }

const EditUser: React.FC<EditUserProps> = ({ tabId, onCreated }) => {
    const { t } = useTranslation()
    const format = Format()
    const formRef = useRef<TTabListFormData | null>(null)
    const [formError, setFormError] = useState({})
    const roles = roleTypeList()
    const tab = findTab(tabId)
    if (!tab) return null // or throw an error
    const [formData, setFormData] = useSyncedState(tab, 'formData', tab.store.initialData)
    const [initialData, setInitialData] = useSyncedState(tab, 'initialData', tab.store.initialData)
    const dirty = useMemo(() => {
        if (!tab?.store?.initialData) return false
        return JSON.stringify(formData) !== JSON.stringify(initialData)
    }, [formData, tab])

    useTabDirty(tab, dirty)

    useTabSaveHandler(tab, () => onSubmit({ preventDefault() { } } as any))
    const newRecord = !tab?.store?.id
    const refetch = async () => {
        if (!tab.store.id) return
        const res: any = await userModel.read(String(tab.store.id))
        if (res?.data) {
            const next = {
                ...(res.data || {}),
                email: res.data?.email ?? '',
                phone: res.data?.phone ?? '',
                password: '',
                confirmPassword: '',
            }
            setFormData(next)
            setInitialData(next)
            tab.store.initialData = next
        }
    }
    const lock = useEditLock({
        table: 'user',
        url: 'user',
        id: String(tab?.store?.id || ''),
        tabId,
        userId: String(user.value?._id || ''),
        userName: String(user.value?.userName || ''),
        enabled: !newRecord,
        onAcquire: refetch,
        onUpdated: refetch,
    })
    useEffect(() => {
        if (newRecord) return
        if (!tab?.store?.id) return

        const socket = getSocket()
        const id = String(tab.store.id)

        const onResourceChanged = (payload: any) => {
            if (!payload) return
            if (payload.table !== 'user') return
            if (String(payload.id || '') !== id) return

            if (payload.action === 'updated') {
                refetch()
            }
        }

        socket.on('resource:changed', onResourceChanged)
        return () => {
            socket.off('resource:changed', onResourceChanged)
        }
    }, [newRecord, tab?.store?.id, tabId, t])

    const isDisabled = user.value?._id === initialData.id
    // Deactivating (operator, 14.10.2026): owner, project leader and administrator,
    // never on one's own account, never on the owner. `blocked` is the payment lock
    // and is only shown. The server checks the same.
    const currentStatus = initialData.status ?? 'active'
    const canChangeStatus =
        !newRecord &&
        !isDisabled &&
        initialData.role !== RoleType.Owner &&
        [RoleType.Administrator, RoleType.Owner, RoleType.ProjectLeader].includes(user.value?.role) &&
        ['active', 'disabled'].includes(currentStatus)
    const statusList = [
        { value: 'active', label: t('Active') },
        { value: 'disabled', label: t('Disabled') },
    ]
    const statusLabel = currentStatus === 'blocked' ? t('Blocked') : currentStatus === 'disabled' ? t('Disabled') : t('Active')
    const canEdit = newRecord || lock.status === 'owned'
    const [projectOptions, setProjectOptions] = useState([])
    const [selectedPermissions, setSelectedPermissions] = useState(['read', 'write', 'delete'])
    //const projectTimeoutRef = useRef<number>();

    const roleLabel = useMemo(() => {
        for (let r of roleTypeList()) {
            if (r.value === initialData.role) return r.label
        }
        return initialData.role
    }, [])

    const loadProjects = useCallback((search: string) => {
        //if (projectTimeoutRef.current) clearTimeout(projectTimeoutRef.current);
        // projectTimeoutRef.current = window.setTimeout(async () => {
        //   const res = await axios.get('/api/projects/autocomplete', { params: { q: search } });
        //   setProjectOptions(res.data.map((p: { name: any; _id: any; }) => ({ label: p.name, value: p._id })));
        // }, 300);
    }, [])

    const handleAssignProjects = async () => {
        if (!formData || formData?.projects?.length === 0) {
            //toaster.push(<Message type="error">Bitte User und Projekte auswählen!</Message>)
            toastMessage(t('Select user and project!'), 'error')
            return
        }

        const permissionsObj = {
            read: selectedPermissions.includes('read'),
            write: selectedPermissions.includes('write'),
            delete: selectedPermissions.includes('delete'),
        }

        await Promise.all(
            formData?.projects?.map(
                (project: any) => console.log(project)
                // axios.post('/api/user-project-role', {
                //   userId: selectedUser,
                //   projectId,
                //   permissions: permissionsObj
                // })
            )
        )

        //toaster.push(<Message type="success">Projekte und Rechte erfolgreich zugewiesen!</Message>)
        toastMessage(t('Projects and permissions successfully assigned!'))
    }

    const hasErrors = () => {
        return false
        // const count = Object.keys(formError).length
        // if (count > 0) console.log('EditUser.hasErrors', formError, count)
        // return count > 0
    }

    const onSubmit = (e: any) => {
        e.preventDefault()

        const saveData = { ...formData }
        delete saveData.subscription
        const id = saveData._id || ''

        // only send changed fields when updating
        if (id) {
            Object.keys(initialData || {}).forEach((k) => {
                if (initialData[k] === saveData[k]) delete saveData[k]
            })

            // typical special-case: don't send empty passwords on edit
            if (!saveData.password) delete saveData.password
            if (!saveData.confirmPassword) delete saveData.confirmPassword
        }

        // return if nothing has changed
        if (Object.keys(saveData).length <= 0) {
            return
        }

        formRef.current.checkAsync().then(async (result: any) => {
            if (!result.hasError) {
                const response = newRecord ? await userModel.create(saveData) : await userModel.update(id, saveData)
                // console.log(response)
                if (response?.data) {
                    toastMessage(t('Successfully saved'))
                    const saved = {
                        ...(formData || {}),
                        ...(response.data || {}),
                        email: response.data?.email ?? formData?.email ?? '',
                        phone: response.data?.phone ?? formData?.phone ?? '',
                        password: '',
                        confirmPassword: '',
                    }

                    // update UI + baseline so dirty becomes false
                    setFormData(saved)
                    setInitialData(saved)
                    tab.store.initialData = saved
                    // a rename shows in the tab too (a new record is renamed below)
                    if (!newRecord) setTabName(tabId, userTabName(saved))

                    // if this was a create, convert the tab into an edit tab + rename header
                    if (newRecord) {
                        const newId = String(response.data._id || '')
                        const firstInitial = String(saved.firstName || '')
                            .trim()
                            .charAt(0)
                        const lastName = String(saved.lastName || '').trim()
                        const nextName = `${firstInitial}. ${lastName}`.trim()

                        const nextDataId = `user-${newId}`

                        // Immutably update the tab entry so FlexLayout title updates immediately
                        tabStore.tabs.value = tabStore.tabs.value.map((t) => {
                            if (t.id !== tabId) return t
                            return {
                                ...t,
                                dataId: nextDataId,
                                name: nextName,
                                store: {
                                    ...(t.store || {}),
                                    id: newId,
                                    resourceId: newId,
                                    formData: saved,
                                    initialData: saved,
                                    isDirty: false,
                                },
                            }
                        })
                    } else {
                        serverLog.info(`User ${user.value.userName} edited user ${response.data.userName}`, {
                            actorID: user.value._id,
                            subjectID: response.data._id,
                        })
                    }

                    onCreated?.(response.data)
                    //update initial data
                    // if (!newRecord) lock.afterSave()
                    getSocket().emit('editing client: resourceUpdated', {
                        table: 'user',
                        id: String(response.data._id || tab.store.id),
                        url: 'user',
                    })
                } else {
                    setFormError({ message: t('Unknown Error') })
                }
            }
        })
    }
    const model = Schema.Model({
        firstName: StringType().isRequired(format.required(t('First name'))),
        lastName: StringType().isRequired(format.required(t('Last name'))),
        userName: StringType()
            .addAsyncRule((value, data) => {
                return new Promise((resolve) => {
                    const _check = async () => {
                        if (!value || !newRecord) return resolve(true)
                        const response: any = await userModel.checkExistingUsernameOrEmail(value, data?._id || '')
                        console.log('Username exists?', response.data.exists)
                        if (response.data.exists && response.data.id === formData._id && response.data.userName == value) {
                            resolve(false)
                        }
                        else {
                            resolve(!response.data.exists)
                        }
                    }
                    _check()
                })
            }, t('User name already in use'))
            .isRequired(format.required(t('User name'))),
        password: StringType()
            .when((schema) => {
                return newRecord ? StringType().isRequired(format.required(t('Password'))) : StringType()
            })
            .addRule(
                (value, data) => {
                    return true
                },
                t('Passwords no match') as string
            ),
        confirmPassword: StringType()
            .when((schema) => {
                return schema.password.value
                    ? StringType().isRequired(format.required(t('Confirm password')))
                    : StringType()
            })
            .addRule((value, data) => value === data.password, t('Passwords no match') as string),
        email: StringType()
            .addAsyncRule(
                (value, data) => {
                    return new Promise((resolve) => {
                        const _check = async () => {
                            let retCode = true
                            if (!newRecord || value) {
                                const response: any = await userModel.checkExistingUsernameOrEmail(value, data?._id || '')
                                console.log('Username exists?', response.data.exists)
                                if (response.data.exists && response.data.id === formData._id && response.data.email == value) {
                                    retCode = true
                                }
                                else {
                                    retCode = !response.data.exists
                                }
                            }
                            console.error('addAsyncRule: ', retCode)
                            resolve(retCode)
                        }
                        _check()
                    })
                },
                t('Email already in use') as string
            )
            .isEmail(t('Invalid email address') as string),
    })

    return (
        <Container>
            <DisableOverlay disabled={!canEdit} banner={lock.banner}>
                <Stack className="tab-stack" spacing={20} alignItems="flex-start">
                    <Panel bordered header={t('User data')}>
                        <Form
                            layout='horizontal'
                            formValue={formData}
                            ref={formRef}
                            model={model}
                            // onChange={handleFormChange}
                            onChange={(data) => {
                                console.log('EditUser.Form.onChange:', data)
                                setFormData({ ...(formData || {}), ...(data || {}) })
                            }}
                            onCheck={(err) => {
                                //setFormError(err)
                                // tabListHeaderChanged({
                                //   action: 'onCheck',
                                //   //data: formData,
                                //   //modified: true,
                                //   error: err,
                                // })
                            }}
                            autoComplete="off"
                            onSubmit={onSubmit}
                        >
                            {roles.length > 0 && (
                                isDisabled
                                    ? <StaticField label={t('Role')} value={roleLabel} type="readonly" />
                                    : <SelectField
                                        name="role"
                                        label={t('Role')}
                                        data={roleTypeList()}
                                        disabled={isDisabled}
                                        fullWidth
                                    />
                            )}
                            {canChangeStatus ? (
                                <SelectField name="status" label={t('Status')} data={statusList} fullWidth />
                            ) : (
                                !newRecord && <StaticField label={t('Status')} value={statusLabel} type="readonly" />
                            )}
                            <TextField name="firstName" label={t('First name')} autoFocus={true} />
                            <TextField name="lastName" label={t('Last name')} />

                            <TextField name="email" label={t('Email')} />
                            <TextField name="phone" label={t('Phone')} />

                            <TextField
                                name="userName"
                                label={t('User name')}
                                autoComplete="new-password"
                                readOnly={!newRecord}
                            />
                            <PasswordField name="password" label={t('Password')} checkStrength={true} />
                            <PasswordField name="confirmPassword" label={t('Confirm password')} />

                            <div style={TabListFooterStyle}>
                                {hasErrors() && (
                                    <span style={TabListFooterErrorWrapperStyle}>
                                        <span style={TabListFooterErrorStyle}>{t('Check your inputs')}</span>
                                    </span>
                                )}
                                <Button onClick={onSubmit} appearance="ghost">
                                    {t('Apply')}
                                </Button>
                            </div>
                        </Form>
                    </Panel>

                    {!newRecord && (
                        <Panel bordered header={t('Assigned projects')}>
                            <UserProjectRelation
                                type={RelationType.Project}
                                id={tab?.store?.id}
                                userRole={formData?.role}
                                buttonText={t('Add project')}
                            />
                        </Panel>
                    )}
                </Stack>
            </DisableOverlay>
        </Container >
    )
}

export default EditUser
